/* app/routes.jsx — каноничные маршруты + карта редиректов (легаси → ново).
   Источник: 02-IA §3 (карта «было→стало»). Всё адресуемо deep-link.

   ХЕНДОФФ → frontend createBrowserRouter([...]) (как в боевом frontend/src/routes.jsx).
   Прототип использует element-API <Routes>/<Route> локального шима (lib/router.jsx, HASH-режим);
   перенос НЕ дословный — element-дерево разворачивается в массив route-объектов:
     <Route path="x" element={<E/>}>…</Route>  →  { path:'x', element:<E/>, children:[…] }
     layout-роут с детьми                       →  { element:<Layout/>, children:[…] } + <Outlet/> внутри Layout
     <Route path="*" element={<Navigate to=…/>} →  redirect-объект (или loader→redirect())
     index-роут                                 →  { index:true, element:… }
   Целевой режим — history (BrowserRouter), а не HASH: на сервере нужен SPA-fallback
   (nginx try_files … /index.html), иначе refresh/deep-link на вложенных путях дадут 404.
   Редиректам ниже проставляем replace явно (react-router Navigate по умолчанию replace=false). */

import React from 'react';
import { GuestOnlyRoute } from './GuestOnlyRoute.jsx';
import { AppShell } from './layouts/AppShell.jsx';
import { useAuth } from './providers/AuthProvider.jsx';
import { AuthLayout } from '../components/patterns/AuthLayout.jsx';
import { Button } from '../components/ui/Button.jsx';
import { sectionRouteDecision } from '../lib/planSections.js';
import { RR } from '../lib/router.jsx';
import { EmailSent } from '../screens/auth/EmailSent.jsx';
import { Forgot } from '../screens/auth/Forgot.jsx';
import { InviteAccept } from '../screens/auth/InviteAccept.jsx';
import { LegalStub } from '../screens/auth/LegalStub.jsx';
import { NewPassword } from '../screens/auth/NewPassword.jsx';
import { SignIn } from '../screens/auth/SignIn.jsx';
import { SignUp } from '../screens/auth/SignUp.jsx';
import { VerifyEmail } from '../screens/auth/VerifyEmail.jsx';
import { VerifyEmailChange } from '../screens/auth/VerifyEmailChange.jsx';
import { CheckoutFail } from '../screens/checkout/CheckoutFail.jsx';
import { CheckoutInvoice } from '../screens/checkout/CheckoutInvoice.jsx';
import { CheckoutMock } from '../screens/checkout/CheckoutMock.jsx';
import { CheckoutSuccess } from '../screens/checkout/CheckoutSuccess.jsx';
import { AdminHub } from '../screens/admin/AdminHub.jsx';
import { CrmScreen } from '../screens/crm/CrmShell.jsx';
import { CrmSettings } from '../screens/crm/CrmSettings.jsx';
import { BroadcastsScreen } from '../screens/broadcasts/BroadcastsScreen.jsx';
import { PlansScreen } from '../screens/sales/PlansScreen.jsx';
import { plansRouteRedirect } from '../screens/sales/plansAccess.js';
import { PaymentLinksScreen } from '../screens/sales/PaymentLinksScreen.jsx';
import { PayByLink } from '../screens/checkout/PayByLink.jsx';
import { BuyPlan } from '../screens/checkout/BuyPlan.jsx';
import { GiftBuy } from '../screens/gift/GiftBuy.jsx';
import { GiftMy } from '../screens/gift/GiftMy.jsx';
import { GiftRedeem } from '../screens/gift/GiftRedeem.jsx';
import { SupportScreen } from '../screens/support/SupportScreen.jsx';
import { UserSupportChat } from '../screens/support/UserSupportChat.jsx';
import { AcademyConsole } from '../screens/academy/AcademyConsole.jsx';
import { LearningHome } from '../screens/academy/LearningHome.jsx';
import { CoursePlayer } from '../screens/academy/CoursePlayer.jsx';
import { CourseBuyPage } from '../screens/academy/CourseBuyPage.jsx';
import { CertificatePage } from '../screens/academy/CertificatePage.jsx';
import { CourseLandingPage } from '../screens/academy/landing/CourseLandingPage.jsx';
import { CoursePreviewPage } from '../screens/academy/landing/CoursePreviewPage.jsx';
import { PublicSupport } from '../screens/support/PublicSupport.jsx';
import { UiKit } from '../screens/dev/UiKit.jsx';
import { IngredientLegacyRedirect } from '../screens/kitchen/IngredientLegacyRedirect.jsx';
import { KitchenScreen } from '../screens/kitchen/KitchenShell.jsx';
import { NotificationsInbox } from '../screens/notifications/NotificationsInbox.jsx';
import { ReportDetailScreen } from '../screens/reports/ReportDetail.jsx';
import { ReportsScreen } from '../screens/reports/ReportsList.jsx';
import { SettingsScreen } from '../screens/settings/SettingsScreen.jsx';
import { ChatScreen } from '../screens/workspace/ChatShell.jsx';
import { UtmCapture } from '../screens/utm/UtmCapture.jsx';

// Аналитика — единственный потребитель recharts; грузим ЛЕНИВО, чтобы чарт-библиотека
// уезжала в отдельный чанк раздела, а не в основной бандл.
const AnalyticsScreen = React.lazy(() =>
  import('../screens/analytics/Analytics.jsx').then((m) => ({ default: m.AnalyticsScreen })));

// Консоль UTM-меток — второй потребитель recharts (график воронки в карточке метки).
// Тоже ЛЕНИВО: статический импорт утащил бы чарт-библиотеку обратно в основной бандл
// и обнулил смысл разделения выше.
const UtmScreen = React.lazy(() =>
  import('../screens/utm/UtmScreen.jsx').then((m) => ({ default: m.UtmScreen })));
const UtmTagDetail = React.lazy(() =>
  import('../screens/utm/UtmTagDetail.jsx').then((m) => ({ default: m.UtmTagDetail })));
// Типы (каналы) меток — экран администратора UTM; делит чанк с разделом.
const UtmTypesScreen = React.lazy(() =>
  import('../screens/utm/UtmTypesScreen.jsx').then((m) => ({ default: m.UtmTypesScreen })));
// «Задачи» — план разработки команды (Админка). Ленивый чанк, как у остальных
// консольных разделов: нужен единицам, в основной бандл не кладём.
const TrackerScreen = React.lazy(() =>
  import('../screens/tracker/TrackerScreen.jsx').then((m) => ({ default: m.TrackerScreen })));

// Публичный демо-стенд бизнес-аналитики (/business-demo) — вне AppShell/гардов,
// без авторизации. Тянет те же тяжёлые iiko-отчёты (recharts) → ленивый чанк.
const BusinessDemoScreen = React.lazy(() =>
  import('../screens/demo/BusinessDemo.jsx').then((m) => ({ default: m.BusinessDemo })));

  const { Routes, Route, Navigate, useParams, useSearchParams } = RR;

  // Гард авторизации: нет user → /signin (replace — не плодим запись в истории)
  function RequireAuth({ children }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/signin" replace />;
    return children;
  }

  // Гард страниц возврата после оплаты: пускает по сессии ЛИБО по токену
  // именной ссылки в `?pl=` (его приносит редирект платёжного шлюза).
  //
  // Счёт на существующее пространство вправе оплатить кто угодно — бухгалтер,
  // учредитель, партнёр, — и аккаунта у него нет. Под обычным RequireAuth он
  // после успешной оплаты улетал на /signin, читал это как «деньги не прошли»
  // и платил второй раз. Экраны при `?pl=` читают состояние платежа
  // токен-скоупом; случайный посетитель без сессии и без токена уходит на вход
  // ровно как прежде.
  function RequireAuthOrPayLink({ children }) {
    const { user } = useAuth();
    const [params] = useSearchParams();
    if (!user && !params.get('pl')) return <Navigate to="/signin" replace />;
    return children;
  }

  // Гард доступа к CRM: только сотрудники с crm_access (группа продаж/суперюзер).
  // crm_access приходит из /api/auth/me (UserSerializer). Нет доступа → на Ассистента.
  function RequireCrmAccess({ children }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/signin" replace />;
    if (!user.crm_access) return <Navigate to="/chat" replace />;
    return children;
  }

  // Гард настройки CRM: только админ (crm.manage_crm). Продавца уводим на доску.
  function RequireCrmAdmin({ children }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/signin" replace />;
    if (!user.crm_admin) return <Navigate to="/console/crm" replace />;
    return children;
  }

  // Гард хаба «Админка» /console: доступ к ЛЮБОМУ модулю (CRM или Рассылки).
  function RequireConsoleAccess({ children }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/signin" replace />;
    if (!user.console_access) return <Navigate to="/chat" replace />;
    return children;
  }

  // Гард модуля «Рассылки»: marketing.manage_broadcasts (группа «Маркетинг»/суперюзер).
  function RequireBroadcastsAccess({ children }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/signin" replace />;
    if (!user.broadcasts_access) return <Navigate to="/console" replace />;
    return children;
  }

  // Гард модуля «UTM-метки»: utm.manage_utm_tags (группа «Маркетинг: UTM-метки»/суперюзер).
  // utm_access приходит из /api/auth/me и уже учитывает рубильник UTM_ENABLED.
  function RequireUtmAccess({ children }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/signin" replace />;
    if (!user.utm_access) return <Navigate to="/console" replace />;
    return children;
  }

  // Гард экрана «Типы меток»: utm.manage_utm_types (группа «Маркетинг: администратор
  // UTM»/суперюзер). Сотрудника с одним правом на метки уводим к его списку — ему
  // там объяснено, кто заводит типы. Флаг тоже учитывает рубильник UTM_ENABLED.
  function RequireUtmTypesAccess({ children }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/signin" replace />;
    if (!user.utm_manage_types) return <Navigate to={user.utm_access ? '/console/utm' : '/console'} replace />;
    return children;
  }

  // Гард операторского модуля «Поддержка»: support.access_support (группа
  // «Поддержка»/суперюзер). Юзерская страница /support — без этого гарда.
  function RequireSupportAccess({ children }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/signin" replace />;
    if (!user.support_access) return <Navigate to="/console" replace />;
    return children;
  }

  // Гард раздела «Тарифы»: любое из двух независимых прав — manage_private_plans
  // (вкладка «Закрытые») или manage_public_plans (вкладка «Публичные»). Право
  // определяет, ПОЧЁМ мы продаём, поэтому отдельно от права выставлять ссылки.
  // Какие вкладки видны — решает сам экран (plansAccess.js), гард лишь не пускает
  // того, у кого нет ни одного. Решение — чистая plansRouteRedirect (покрыта тестом).
  function RequirePlansAccess({ children }) {
    const { user } = useAuth();
    const redirectTo = plansRouteRedirect(user);
    if (redirectTo) return <Navigate to={redirectTo} replace />;
    return children;
  }

  // Гард «Ссылки на оплату»: subscriptions.issue_payment_links.
  function RequirePaymentLinksAccess({ children }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/signin" replace />;
    if (!user.payment_links_access) return <Navigate to="/console" replace />;
    return children;
  }

  // Гард «Задач» (план разработки): право tracker.access_tracker → user.tracker_access.
  function RequireTrackerAccess({ children }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/signin" replace />;
    if (!user.tracker_access) return <Navigate to="/console" replace />;
    return children;
  }

  // Гард модуля «Академия» в консоли: методист (manage_academy) или куратор
  // (review_homework) → academy_access (при выключенном ACADEMY_ENABLED бэк
  // отдаёт его false). Ученическая часть /learning — гейт ниже, по рубильнику.
  function RequireAcademyAccess({ children }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/signin" replace />;
    if (!user.academy_access) return <Navigate to="/console" replace />;
    return children;
  }

  // Экран выключенного раздела: прямой переход должен ОБЪЯСНЯТЬ, а не
  // притворяться пустым каталогом («курсов нет») и не молча редиректить —
  // человек с ссылкой на урок обязан понять, что произошло (PL-7).
  function AcademyDisabled() {
    const { useNavigate } = RR;
    const navigate = useNavigate();
    return (
      <div className="section-page">
        <div className="section-body">
          <div className="state">
            <h2 className="t-h2">Раздел «Обучение» отключён</h2>
            <p className="state__body text-dim">
              Академия временно недоступна на платформе. Ваши записи на курсы и прогресс
              сохранены — раздел откроется, как только его включат.
            </p>
            <Button variant="primary" onClick={() => navigate('/chat')}>На главную</Button>
          </div>
        </div>
      </div>
    );
  }

  // Рубильник раздела «Обучение» (ACADEMY_ENABLED → academy_enabled в /api/auth/me).
  function RequireAcademyEnabled({ children }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/signin" replace />;
    if (!user.academy_enabled) return <AcademyDisabled />;
    return children;
  }

  // Раздел, закрытый ТАРИФОМ пространства (workspace.sections.<section> === false).
  // Пункт меню уже исчез (navConfig), а прямую ссылку — закладку, письмо, легаси
  // /dashboard — молча уводим к Ассистенту: страница «раздел отключён» обещала бы
  // функцию, которой у этой команды нет. Гард читает activeWorkspace из контекста, поэтому
  // переключение пространства ВНУТРИ закрытого раздела тоже уводит сразу. Серверный гейт
  // (403 section_disabled) стоит независимо — это лишь чтобы не показывать тупик.
  // Пока пространство не определилось (нет sections) — пускаем: поведение до флагов.
  function RequireSectionEnabled({ section, children }) {
    const { activeWorkspace } = useAuth();
    const decision = sectionRouteDecision(section, activeWorkspace);
    if (!decision.allow) return <Navigate to={decision.redirectTo} replace />;
    return children;
  }

  // Перемонтировать экран при смене активного пространства: key по activeWorkspace.id
  // → React сбрасывает поддерево, и экран перезапрашивает данные нового ws (иначе
  // useFakeQuery со статичным ключом отдавал бы кэш прошлого пространства).
  // Применяем к разделам, чьи данные привязаны к ws: Кухня, Аналитика, Отчёты, Ресторан.
  // (Ассистент/чат и Настройки переключение обрабатывают сами — их не оборачиваем.)
  function RemountOnWorkspace({ children }) {
    const { activeWorkspace } = useAuth();
    return React.cloneElement(children, { key: activeWorkspace ? String(activeWorkspace.id) : 'none' });
  }

  // Легаси ТТК-роуты прода → drawer-канон /kitchen?tab=recipes&edit=ttk-{id} (02-IA §4.2).
  // /ttk/new — открыть редактор новой ТТК; /ttk/:id и /ttk/:id/edit — конкретную карту.
  function TtkNewRedirect() { return <Navigate to="/kitchen?tab=recipes&edit=ttk-new" replace />; }
  function TtkLegacyRedirect() { const { id } = useParams(); return <Navigate to={'/kitchen?tab=recipes&edit=ttk-' + id} replace />; }

  // Легаси /restaurant (раздел убран из сайдбара) → Настройки → «Для бизнеса»:
  // профиль → &biz=restaurant; база знаний (?tab=documents или ?doc=ID) → &biz=knowledge
  // (+&doc= для подсветки карточки). Старые ссылки живут в сообщениях чата — редирект обязателен.
  function RestaurantLegacyRedirect() {
    const [params] = useSearchParams();
    const doc = params.get('doc');
    const kb = doc != null || params.get('tab') === 'documents';
    const to = '/settings?tab=business&biz=' + (kb ? 'knowledge' : 'restaurant')
      + (doc ? '&doc=' + encodeURIComponent(doc) : '');
    return <Navigate to={to} replace />;
  }

  // Заглушка детальной отчёта удалена — S8 даёт реальный ReportDetailScreen (ReportDetailScreen).

  // Карта легаси-редиректов (старые пути прода → новые разделы)
  const REDIRECTS = [
    ['/', '/chat'],
    ['/login', '/signin'],
    // /dashboard → /analytics → (гард RequireSectionEnabled) → /chat, если тариф закрыл раздел
    ['/dashboard', '/analytics'],
    ['/recipes', '/kitchen'],
    ['/products', '/kitchen'],
    ['/menu', '/kitchen'],
    ['/account', '/settings'],
    ['/profile', '/settings?tab=profile'],
    ['/crm', '/console/crm'],
  ];

  function NotFound() {
    const { useNavigate } = RR;
    const navigate = useNavigate();
    return (
      <div className="section-page">
        <div className="section-body">
          <div className="state">
            <div className="state__glyph state__glyph--warn"><span className="num" style={{ fontSize: 22, fontWeight: 600 }}>404</span></div>
            <h2 className="t-h2">Страница не найдена</h2>
            <p className="state__body text-dim">Похоже, такого раздела нет. Вернитесь к Ассистенту.</p>
            <Button variant="primary" onClick={() => navigate('/chat')}>На главную</Button>
          </div>
        </div>
      </div>
    );
  }

  function AppRoutes() {
    return (
      <Routes>
        {/* легаси-редиректы (replace явно — чтобы редирект не оставался в истории) */}
        {REDIRECTS.map(([from, to]) => (
          <Route key={from} path={from} element={<Navigate to={to} replace />} />
        ))}

        {/* неавторизованная зона (S12) — AuthLayout + GuestOnlyRoute (воронка входа/регистрации) */}
        <Route path="/" element={<GuestOnlyRoute><AuthLayout /></GuestOnlyRoute>}>
          <Route path="signin" element={<SignIn />} />
          <Route path="signup" element={<SignUp />} />
          <Route path="forgot" element={<Forgot />} />
          <Route path="reset-password" element={<NewPassword />} />
          <Route path="email-sent" element={<EmailSent />} />
        </Route>

        {/* auth-экраны, доступные всем (вход по ссылке из письма / приглашение) — AuthLayout без гарда */}
        <Route path="/" element={<AuthLayout />}>
          <Route path="verify-email" element={<VerifyEmail />} />
          <Route path="verify-email-change" element={<VerifyEmailChange />} />
          <Route path="invite/accept/:token" element={<InviteAccept />} />
        </Route>

        {/* правовые заглушки (живые ссылки оферты/политики — фикс №21), вне AuthLayout */}
        <Route path="/legal/offer" element={<LegalStub />} />
        <Route path="/legal/privacy" element={<LegalStub />} />

        {/* публичная поддержка — БЕЗ авторизации (гость по X-Support-Token), вне AppShell */}
        <Route path="/help" element={<PublicSupport />} />

        {/* публичный демо-стенд бизнес-аналитики — БЕЗ авторизации, вне AppShell/гардов */}
        <Route path="/business-demo" element={(
          <React.Suspense fallback={<div className="app-shell" aria-busy="true" />}>
            <BusinessDemoScreen />
          </React.Suspense>
        )} />

        {/* короткая ссылка UTM-метки /r/<code> — БЕЗ авторизации и вне AppShell.
            Штатно её обслуживает бэкенд (nginx location ^~ /r/ → Django), но снипеты nginx
            у нас ставятся руками и уже терялись: без блока путь уедет в SPA-fallback,
            и переход доберёт этот экран через /api/utm/hit/. */}
        <Route path="/r/:code" element={<UtmCapture />} />
        {/* ссылка метки на покупку тарифа: /r/<code>/buy/<slug> → переход засчитан как
            'buy' со слагом → страница оплаты /buy/<slug> (адрес отдаёт бэкенд; тариф не в
            продаже — регистрация). Порядок объявления здесь ни на что не влияет: в отличие
            от re_path бэкенда, lib/router.jsx выбирает ветку по специфичности (score —
            статический сегмент весит больше параметра), а четырёхсегментный адрес общий
            /r/:code/:to не поймает и так. Что /r/<code>/buy/<slug>, /r/<code>/<to> и
            /r/<code> попадают в UtmCapture со своими параметрами, проверяет поведенческий
            тест на настоящем роутере (screens/utm/UtmPlanLinks.test.jsx). */}
        <Route path="/r/:code/buy/:slug" element={<UtmCapture />} />
        {/* ссылка метки на страницу курса Академии: /r/<code>/course/<slug> → переход
            засчитан как 'course' со слагом → лендинг курса /course/<slug> (адрес отдаёт
            бэкенд; курс снят с публикации или Академия выключена — регистрация).
            Сегмент `course` статический и в параметры не попадает — назначение передаёт
            пропс `dest`. Слаг бывает кириллическим: браузер отдаёт путь в процентах,
            роутер раскодирует параметр. Поведение — тот же тест на настоящем роутере
            (screens/utm/UtmCourseLinks.test.jsx). */}
        <Route path="/r/:code/course/:slug" element={<UtmCapture dest="course" />} />
        {/* тот же фолбэк для ссылок с назначением: /r/<code>/site — лендинг,
            /r/<code>/demo — демо-стенд. Без этого роута ссылка на лендинг при
            пропавшем nginx-блоке уводила бы на регистрацию — не туда, куда
            обещал маркетолог партнёру. */}
        <Route path="/r/:code/:to" element={<UtmCapture />} />

        {/* публичный сертификат — открыт по ссылке любому (БЕЗ авторизации), вне AppShell */}
        <Route path="/certificate/:publicId" element={<CertificatePage />} />

        {/* лендинг курса — ЕДИНАЯ ссылка /course/:slug (#16), верхний уровень (НЕ под
            RequireAuth): аноним → standalone, авторизованный → внутри AppShell.
            Оболочку выбирает сам CourseLandingPage по факту авторизации. */}
        <Route path="/course/:slug" element={<CourseLandingPage />} />

        {/* предпросмотр лендинга из черновика (методист) — standalone, в новой вкладке.
            Гард academy-доступа (manage_academy/review_homework). */}
        <Route path="/academy/course/:id/preview" element={<RequireAcademyAccess><CoursePreviewPage /></RequireAcademyAccess>} />

        {/* Онбординг теперь — игровой оверлей поверх приложения (app/onboarding/, монтируется
            в AppShell и авто-стартует для нового пользователя). Легаси-путь /onboarding ведёт в чат. */}
        <Route path="/onboarding" element={<Navigate to="/chat" replace />} />

        {/* dev-витрина — вне гарда, для приёмки */}
        <Route path="/dev/uikit" element={<UiKit />} />

        {/* Именная ссылка на оплату от менеджера — БЕЗ RequireAuth.
            Гард увёл бы на /signin, потеряв токен из пути, и клиент, не понявший,
            куда попал, просто закрыл бы вкладку. Экран сам показывает счёт
            анониму (в урезанном виде) и ведёт на вход, сохраняя, куда вернуться. */}
        <Route path="/pay/:token" element={<PayByLink />} />

        {/* Публичная ссылка покупки личного тарифа (соцсети, акции) — БЕЗ RequireAuth:
            аноним регистрируется прямо на странице, письмо подтверждения возвращает
            сюда же и сразу входит (VerifyEmail). */}
        <Route path="/buy/:slug" element={<BuyPlan />} />

        {/* Активация подарочного сертификата — БЕЗ RequireAuth, по тем же
            причинам, что и именная ссылка выше: сюда приходит ПОСТОРОННИЙ
            человек по ссылке из письма, гард увёл бы его на /signin и потерял
            код из адреса. Экран сам предлагает вход или регистрацию, сохранив
            код в sessionStorage. Статический сегмент `redeem` объявлен рядом с
            динамическим `:code` — шим роутера ранжирует ветки по числу
            статических сегментов, так что перехвата не будет. */}
        <Route path="/gift/redeem" element={<GiftRedeem />} />
        <Route path="/gift/:code" element={<GiftRedeem />} />

        {/* Checkout — вне оболочки AppShell (CheckoutLayout без сайдбара), требует авторизации */}
        <Route path="/checkout/mock/:invoiceId" element={<RequireAuthOrPayLink><CheckoutMock /></RequireAuthOrPayLink>} />
        {/* Три страницы возврата после оплаты пускают и по токену счёта (`?pl=`):
            платить по именной ссылке вправе тот, у кого аккаунта нет. */}
        <Route path="/checkout/invoice/:invoice_id" element={<RequireAuthOrPayLink><CheckoutInvoice /></RequireAuthOrPayLink>} />
        <Route path="/checkout/success" element={<RequireAuthOrPayLink><CheckoutSuccess /></RequireAuthOrPayLink>} />
        <Route path="/checkout/fail" element={<RequireAuthOrPayLink><CheckoutFail /></RequireAuthOrPayLink>} />

        {/* рабочее пространство */}
        <Route path="/" element={<RequireAuth><AppShell /></RequireAuth>}>
          <Route path="gift" element={<GiftBuy />} />
          <Route path="gift/my" element={<GiftMy />} />
          <Route path="chat" element={<ChatScreen />} />
          <Route path="chat/:chatId" element={<ChatScreen />} />
          <Route path="kitchen" element={<RemountOnWorkspace><KitchenScreen /></RemountOnWorkspace>} />
          <Route path="kitchen/:id" element={<IngredientLegacyRedirect />} />
          {/* legacy ТТК-роуты прода → drawer-канон (02-IA §4.2) */}
          <Route path="kitchen/recipes/:id" element={<TtkLegacyRedirect />} />
          <Route path="ttk/new" element={<TtkNewRedirect />} />
          <Route path="ttk/:id/edit" element={<TtkLegacyRedirect />} />
          <Route path="ttk/:id" element={<TtkLegacyRedirect />} />
          <Route path="analytics" element={<RemountOnWorkspace><RequireSectionEnabled section="analytics"><React.Suspense fallback={<div className="section-page" aria-busy="true" />}><AnalyticsScreen /></React.Suspense></RequireSectionEnabled></RemountOnWorkspace>} />
          <Route path="reports" element={<RemountOnWorkspace><ReportsScreen /></RemountOnWorkspace>} />
          <Route path="reports/:id" element={<RemountOnWorkspace><ReportDetailScreen /></RemountOnWorkspace>} />
          {/* CRM — глобальный раздел отдела продаж (НЕ привязан к пространству, без RemountOnWorkspace) */}
          {/* Админка — хаб внутренних сервисов (staff). Путь /console, НЕ /admin —
              по /admin/ живёт Django-админка. Модули: CRM, Рассылки. */}
          <Route path="console" element={<RequireConsoleAccess><AdminHub /></RequireConsoleAccess>} />
          <Route path="console/crm" element={<RequireCrmAccess><CrmScreen /></RequireCrmAccess>} />
          {/* Конструктор воронок «в одном месте» — только админ (manage_crm) */}
          <Route path="console/crm/settings" element={<RequireCrmAdmin><CrmSettings /></RequireCrmAdmin>} />
          <Route path="console/crm/settings/:pipelineId" element={<RequireCrmAdmin><CrmSettings /></RequireCrmAdmin>} />
          {/* Рассылки — модуль маркетинга (marketing.manage_broadcasts) */}
          <Route path="console/broadcasts" element={<RequireBroadcastsAccess><BroadcastsScreen /></RequireBroadcastsAccess>} />
          {/* Поддержка — операторский модуль (support.access_support) */}
          <Route path="console/support" element={<RequireSupportAccess><SupportScreen /></RequireSupportAccess>} />
          {/* UTM-метки — модуль маркетинга (utm.manage_utm_tags), ленивый чанк с recharts */}
          <Route path="console/utm" element={<RequireUtmAccess><React.Suspense fallback={<div className="section-page" aria-busy="true" />}><UtmScreen /></React.Suspense></RequireUtmAccess>} />
          {/* Статический `types` стоит ПЕРЕД `:tagId`: иначе адрес типов поймала бы карточка метки */}
          <Route path="console/utm/types" element={<RequireUtmTypesAccess><React.Suspense fallback={<div className="section-page" aria-busy="true" />}><UtmTypesScreen /></React.Suspense></RequireUtmTypesAccess>} />
          <Route path="console/utm/:tagId" element={<RequireUtmAccess><React.Suspense fallback={<div className="section-page" aria-busy="true" />}><UtmTagDetail /></React.Suspense></RequireUtmAccess>} />

          {/* Тарифы — вкладки «Закрытые» | «Публичные», каждая по своему праву */}
          <Route path="console/plans" element={<RequirePlansAccess><PlansScreen /></RequirePlansAccess>} />
          <Route path="console/payment-links" element={<RequirePaymentLinksAccess><PaymentLinksScreen /></RequirePaymentLinksAccess>} />
          {/* Академия — модуль обучения в консоли (методист/куратор) */}
          <Route path="console/academy" element={<RequireAcademyAccess><AcademyConsole /></RequireAcademyAccess>} />
          {/* Задачи — план разработки команды (tracker.access_tracker), не привязан к пространству */}
          <Route path="console/tasks" element={<RequireTrackerAccess><React.Suspense fallback={<div className="section-page" aria-busy="true" />}><TrackerScreen /></React.Suspense></RequireTrackerAccess>} />
          {/* Обучение — раздел ученика (доступен любому авторизованному, пока
              включён рубильник ACADEMY_ENABLED) */}
          <Route path="learning" element={<RequireAcademyEnabled><LearningHome /></RequireAcademyEnabled>} />
          <Route path="learning/c/:id" element={<RequireAcademyEnabled><CoursePlayer /></RequireAcademyEnabled>} />
          <Route path="learning/buy/:invoiceId" element={<RequireAcademyEnabled><CourseBuyPage /></RequireAcademyEnabled>} />
          {/* legacy /restaurant → Настройки → «Для бизнеса» (профиль / база знаний) */}
          <Route path="restaurant" element={<RestaurantLegacyRedirect />} />
          <Route path="notifications" element={<NotificationsInbox />} />
          {/* Чат поддержки пользователя — без console-гарда (доступен любому авторизованному) */}
          <Route path="support" element={<UserSupportChat />} />
          <Route path="settings" element={<SettingsScreen />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    );
  }

export { AppRoutes };
export { REDIRECTS };
